import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { publicApi } from "../lib/api";
import { DAYS, formatDay, openStatus } from "../lib/hours";
import ListingCard from "../components/ListingCard";
import Breadcrumb from "../components/Breadcrumb";
import { PageSEO } from "../components/PageSEO";
import {
  MapPin, Phone, Mail, MessageCircle, Globe, Instagram, Facebook, Youtube, Linkedin,
  ArrowLeft, Loader2, Clock, Share2, Copy, Check, Ticket, ShoppingBag, BookOpen, Music,
  ChevronLeft, ChevronRight, X, Play, Camera
} from "lucide-react";

/**
 * ListingDetail — public business profile page.
 *
 * =====================================================================
 * PARITY PRINCIPLE (design brief · non-negotiable)
 * =====================================================================
 * Every published business — regardless of category, seniority, or admin
 * favouritism — must render with the SAME structure, spacing, hierarchy and
 * order of sections. What differs between listings is only what the business
 * itself has uploaded (photos, video, description). No "premium" layouts,
 * no "richer" cards. If you're tempted to add an optional block that some
 * listings will have and others won't (parking, distance, transport, curiosities,
 * territory info…), DON'T: it creates visible disparity between associates.
 *
 * The standard sections shown, in fixed order, are:
 *   1. Hero (cover + logo + name + slogan + CTAs)
 *   2. Galleria (photos; video appended as the LAST tile when present)
 *   3. Chi siamo
 *   4. Servizi
 *   5. Mappa
 *   6. Sidebar → Contatti + Social + Orari
 *   7. Altre attività della stessa categoria
 *
 * A section is either fully absent when the business has no data for it, or
 * fully rendered with the same visual weight as on any other listing. There
 * is no in-between "partial" state.
 */
const ytEmbed = (url) => {
  const m = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};
const vimeoEmbed = (url) => {
  const m = url?.match(/vimeo\.com\/(\d+)/);
  return m ? `https://player.vimeo.com/video/${m[1]}` : null;
};
const videoEmbed = (url) => ytEmbed(url) || vimeoEmbed(url) || null;
const videoThumb = (url) => {
  const yt = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
  if (yt) return `https://i.ytimg.com/vi/${yt[1]}/hqdefault.jpg`;
  return null;
};

marked.setOptions({ breaks: true, gfm: true });

// Sanitize Markdown-rendered HTML before injection.
// Prevents XSS via user-controlled business descriptions (originally supplied
// through the public /diventa-socio form and later approved by an admin).
// DOMPurify config: strip <script>, event handlers (onclick, onerror, ...),
// javascript: URLs and other dangerous constructs while keeping standard
// text/formatting/link markup produced by marked().
const _sanitizeConfig = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "em", "b", "i", "u", "s", "ul", "ol", "li", "a",
    "blockquote", "code", "pre", "h1", "h2", "h3", "h4", "h5", "h6",
    "hr", "img", "table", "thead", "tbody", "tr", "th", "td", "span", "div",
  ],
  ALLOWED_ATTR: ["href", "title", "alt", "src", "target", "rel", "class"],
  ALLOWED_URI_REGEXP: /^(https?:|mailto:|tel:|\/|#)/i,
};
const renderMarkdown = (source) =>
  DOMPurify.sanitize(marked.parse(source || ""), _sanitizeConfig);

// JSON-LD safe stringify: escape sequences that would let attacker-controlled
// content break out of the <script> tag. `</script>` closes the block early,
// so we neutralize both the `<` and `/`.
const safeJsonLd = (obj) =>
  JSON.stringify(obj)
    .replace(/</g, "\\u003c")
    .replace(/-->/g, "--\\u003e")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");

const Loading = () => <div className="pt-40 min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#12964A]" /></div>;

const ErrorView = ({ msg }) => (
  <div className="pt-40 min-h-[60vh] px-6"><div className="max-w-2xl mx-auto text-center">
    <h1 className="font-display font-bold text-4xl text-[#1F2937]">{msg}</h1>
    <Link to="/attivita" className="inline-flex items-center gap-2 mt-8 text-[#12964A] hover:underline"><ArrowLeft className="w-4 h-4" /> Torna alle attività</Link>
  </div></div>
);

const ActionBtn = ({ href, icon: Icon, label, primary, target }) => (
  <a href={href} target={target || (href?.startsWith("http") ? "_blank" : undefined)} rel="noreferrer" className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${primary ? "bg-[#12964A] hover:bg-[#0E7C3D] text-white" : "bg-white text-[#1F2937] border border-[#E5E7EB] hover:border-[#12964A]"}`}>
    <Icon className="w-4 h-4" /> {label}
  </a>
);

// Lightbox — Apple/Airbnb-grade UX:
// • close X always visible (glass, top-right, above image); backdrop click; ESC; swipe-down (mobile).
// • arrow nav (desktop) only when >1 image; keyboard ←/→; horizontal swipe (mobile).
// • counter (4/9) bottom-center; scrollable thumbnail strip when >=3 images.
// • body scroll locked while open, position preserved on close; focus trapped, returned on close.
// • fade + micro-zoom on open (220ms), fade-out on close (respects prefers-reduced-motion).
// • preload prev/next; lazy loading via native <img loading> for thumbs.
const Lightbox = ({ items, index, onClose, onPrev, onNext, onSelect }) => {
  const open = index >= 0 && !!items[index];
  const rootRef = useRef(null);
  const closeBtnRef = useRef(null);
  const previouslyFocused = useRef(null);
  const touchStart = useRef(null); // {x, y, t}
  const [entered, setEntered] = useState(false);
  const [closing, setClosing] = useState(false);

  // Lock body scroll (position-fixed technique to preserve scrollY exactly).
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const html = document.documentElement;
    const prev = {
      position: body.style.position, top: body.style.top, left: body.style.left,
      width: body.style.width, overflow: body.style.overflow,
      htmlScrollBehavior: html.style.scrollBehavior,
    };
    // Disable smooth-scroll so the restore below is instant/exact
    html.style.scrollBehavior = "auto";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      // Restore scroll to the exact pre-open Y (no `scrollY` state dep drift)
      window.scrollTo(0, scrollY);
      html.style.scrollBehavior = prev.htmlScrollBehavior;
    };
  }, [open]);

  // Enter animation + focus trap + focus restore.
  useEffect(() => {
    if (!open) { setEntered(false); return; }
    previouslyFocused.current = document.activeElement;
    setEntered(false);
    const t = window.requestAnimationFrame(() => setEntered(true));
    const focusTimeout = window.setTimeout(() => closeBtnRef.current?.focus(), 40);
    return () => {
      window.cancelAnimationFrame(t);
      window.clearTimeout(focusTimeout);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  const requestClose = React.useCallback(() => {
    if (closing) return;
    setClosing(true);
    window.setTimeout(() => { setClosing(false); onClose(); }, 180);
  }, [closing, onClose]);

  // Keyboard handling (global while open): ESC, ←, →, Tab (focus trap)
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "Escape") { e.preventDefault(); requestClose(); }
      else if (e.key === "ArrowRight" && items.length > 1) { e.preventDefault(); onNext(); }
      else if (e.key === "ArrowLeft" && items.length > 1) { e.preventDefault(); onPrev(); }
      else if (e.key === "Tab") {
        // Focus trap: keep focus inside the root
        const root = rootRef.current; if (!root) return;
        const focusables = root.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])');
        if (!focusables.length) return;
        const first = focusables[0]; const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, items.length, onPrev, onNext, requestClose]);

  // Preload adjacent images (only for image kind, using DOM Image).
  useEffect(() => {
    if (!open) return;
    const preload = (i) => {
      const it = items[i]; if (!it || it.kind !== "image") return;
      const img = new Image(); img.src = it.url;
    };
    if (items.length > 1) {
      preload((index + 1) % items.length);
      preload((index - 1 + items.length) % items.length);
    }
  }, [open, index, items]);

  // Swipe gestures (single-touch): horizontal → prev/next, vertical-down → close
  const onTouchStart = (e) => {
    const t = e.touches[0]; touchStart.current = { x: t.clientX, y: t.clientY, at: Date.now() };
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    const dt = Date.now() - touchStart.current.at;
    touchStart.current = null;
    if (dt > 700) return; // ignore slow drags
    const abx = Math.abs(dx), aby = Math.abs(dy);
    if (aby > 90 && aby > abx * 1.2 && dy > 0) { requestClose(); return; } // swipe down
    if (abx > 60 && abx > aby * 1.2 && items.length > 1) {
      if (dx < 0) onNext(); else onPrev();
    }
  };

  if (!open) return null;
  const it = items[index];
  const showNav = items.length > 1;
  const showStrip = items.length > 2;

  return (
    <div
      ref={rootRef}
      role="dialog" aria-modal="true" aria-label="Galleria immagini"
      onClick={requestClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className={`fixed inset-0 z-[70] flex flex-col items-center justify-center transition-opacity duration-[220ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${entered && !closing ? "opacity-100" : "opacity-0"}`}
      style={{ background: "rgba(8,10,12,0.92)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}
      data-testid="lightbox-root"
    >
      {/* Close button — glass dark, always visible above any photo. Sits in a safe area on mobile via padding-top env. */}
      <div
        className="pointer-events-none absolute top-0 left-0 right-0 z-20 flex justify-end px-4 sm:px-6"
        style={{ paddingTop: "max(16px, env(safe-area-inset-top))" }}
      >
        <button
          ref={closeBtnRef}
          onClick={(e) => { e.stopPropagation(); requestClose(); }}
          aria-label="Chiudi galleria"
          data-testid="lightbox-close"
          className="pointer-events-auto w-11 h-11 sm:w-12 sm:h-12 rounded-full grid place-items-center text-white bg-black/55 hover:bg-black/75 border border-white/15 shadow-[0_2px_6px_rgba(0,0,0,0.35),0_10px_30px_rgba(0,0,0,0.45)] backdrop-blur-md transition-all duration-200 hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-black/60"
        >
          <X className="w-5 h-5" strokeWidth={2.25} />
        </button>
      </div>

      {/* Prev/Next arrows — desktop hover-visible, mobile relies on swipe */}
      {showNav && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            aria-label="Immagine precedente"
            data-testid="lightbox-prev"
            className="hidden sm:grid absolute z-20 left-4 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full place-items-center text-white bg-black/45 hover:bg-black/70 border border-white/12 backdrop-blur-md shadow-[0_10px_24px_rgba(0,0,0,0.35)] transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            aria-label="Immagine successiva"
            data-testid="lightbox-next"
            className="hidden sm:grid absolute z-20 right-4 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full place-items-center text-white bg-black/45 hover:bg-black/70 border border-white/12 backdrop-blur-md shadow-[0_10px_24px_rgba(0,0,0,0.35)] transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Media container — DOES NOT stop propagation on itself (so clicks on the
          dark backdrop that pass through empty regions bubble to root → requestClose).
          stopPropagation is placed only on the actual media element (image / video). */}
      <div
        className={`relative w-full h-full flex items-center justify-center px-4 sm:px-16 transition-transform duration-[220ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${entered && !closing ? "scale-100" : "scale-[0.985]"}`}
      >
        {it.kind === "video" ? (
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl aspect-video rounded-2xl overflow-hidden bg-black shadow-[0_30px_80px_rgba(0,0,0,0.55)]">
            {it.embed ? (
              <iframe title={it.title || "video"} src={it.embed} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="w-full h-full" />
            ) : (
              <video controls autoPlay preload="metadata" className="w-full h-full"><source src={it.url} /></video>
            )}
          </div>
        ) : (
          <img
            key={it.url}
            src={it.url}
            alt={it.alt || ""}
            loading="lazy"
            data-testid="lightbox-image"
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[78vh] sm:max-h-[80vh] object-contain rounded-xl shadow-[0_30px_80px_rgba(0,0,0,0.55)] select-none"
            draggable={false}
          />
        )}
        {(it.caption || it.title) && (
          <div onClick={(e) => e.stopPropagation()} className="absolute left-1/2 -translate-x-1/2 bottom-24 sm:bottom-28 text-center text-white/85 text-sm max-w-2xl px-4">
            {it.caption || it.title}
          </div>
        )}
      </div>

      {/* Counter — bottom center, elegant and non-invasive */}
      <div
        className="absolute left-1/2 -translate-x-1/2 text-white/70 text-[13px] tabular-nums tracking-wider pointer-events-none select-none"
        style={{ bottom: showStrip ? "112px" : "max(20px, env(safe-area-inset-bottom))" }}
        data-testid="lightbox-counter"
      >
        {index + 1} <span className="text-white/40">/</span> {items.length}
      </div>

      {/* Thumbnail strip — visible when 3+ items */}
      {showStrip && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute left-0 right-0 flex justify-center"
          style={{ bottom: "max(20px, env(safe-area-inset-bottom))" }}
        >
          <div className="flex gap-2 overflow-x-auto max-w-full px-4 py-1 no-scrollbar">
            {items.map((t, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); onSelect?.(i); }}
                aria-label={`Vai all'immagine ${i + 1}`}
                data-testid={`lightbox-thumb-${i}`}
                className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all duration-200 ${i === index ? "ring-2 ring-[#12964A] ring-offset-2 ring-offset-black scale-100" : "opacity-60 hover:opacity-100 scale-[0.96] hover:scale-100"}`}
              >
                <img src={t.thumb || t.url} alt="" loading="lazy" className="w-full h-full object-cover" />
                {t.kind === "video" && (
                  <span className="absolute inset-0 grid place-items-center bg-black/25">
                    <Play className="w-4 h-4 text-white fill-white" />
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Gallery tile — used inside the mosaic. Shows video overlay if kind='video'
const GalleryTile = ({ item, onClick, className, moreCount }) => (
  <button onClick={onClick} className={`group relative overflow-hidden bg-slate-100 ${className}`} aria-label={item.kind === "video" ? "Riproduci video" : "Apri foto"}>
    <img src={item.thumb || item.url} alt={item.alt || ""} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    {item.kind === "video" && (
      <>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/95 shadow-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
            <Play className="w-6 h-6 text-[#12964A] fill-[#12964A] ml-0.5" />
          </div>
        </div>
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/60 text-white text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full backdrop-blur">
          Video
        </div>
      </>
    )}
    {moreCount > 0 && (
      <div className="absolute inset-0 bg-black/55 text-white flex flex-col items-center justify-center backdrop-blur-sm">
        <Camera className="w-6 h-6 mb-1.5" />
        <div className="text-xl font-medium">+{moreCount}</div>
        <div className="text-[11px] uppercase tracking-[0.15em] text-white/80 mt-1">Vedi tutte</div>
      </div>
    )}
  </button>
);

const ListingDetail = () => {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(-1);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    publicApi.getBusiness(id).then(async (r) => {
      if (cancelled) return;
      setB(r.data);
      const catSlug = r.data?.category?.slug;
      if (catSlug) {
        try {
          const rel = await publicApi.listBusinesses({ category: catSlug, limit: 4 });
          if (!cancelled) setRelated((rel.data || []).filter((x) => x.slug !== r.data.slug).slice(0, 3));
        } catch (e) { /* ignore */ }
      }
    }).catch((err) => {
      const st = err?.response?.status;
      setError(st === 404 ? "Attività non trovata" : (err?.response?.data?.detail || "Errore caricamento"));
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const status = useMemo(() => openStatus(b?.opening_hours), [b?.opening_hours]);
  const vis = b?.contact_visibility || {};
  const show = (k) => vis[k] !== false && !!b?.[k];

  // Merge gallery photos + videos into single media array; videos always last
  const mediaItems = useMemo(() => {
    const items = [];
    (b?.gallery || []).forEach((g) => g?.url && items.push({ kind: "image", url: g.url, alt: g.alt || "", caption: g.caption || "" }));
    (b?.videos || []).forEach((v) => {
      if (!v?.url) return;
      const embed = videoEmbed(v.url);
      const thumb = videoThumb(v.url) || (b?.cover_image_url || b?.logo_url || "");
      items.push({ kind: "video", url: v.url, embed, thumb, title: v.title || "" });
    });
    return items;
  }, [b?.gallery, b?.videos, b?.cover_image_url, b?.logo_url]);

  if (loading) return <Loading />;
  if (error || !b) return <ErrorView msg={error || "Attività non trovata"} />;

  const cover = b.cover_image_url || b.logo_url || "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1600&q=80";
  const wa = b.whatsapp ? b.whatsapp.replace(/[^0-9]/g, "") : "";
  const mapsUrl = (b.latitude && b.longitude)
    ? `https://www.google.com/maps/search/?api=1&query=${b.latitude},${b.longitude}`
    : (b.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.address)}` : null);
  const osmSrc = (b.latitude && b.longitude)
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${b.longitude - 0.005}%2C${b.latitude - 0.005}%2C${+b.longitude + 0.005}%2C${+b.latitude + 0.005}&layer=mapnik&marker=${b.latitude}%2C${b.longitude}`
    : null;
  const services = b.services || [];

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) { /* */ }
  };

  const jsonLd = {
    "@context": "https://schema.org", "@type": "LocalBusiness",
    name: b.name, image: cover, telephone: b.phone || undefined, email: b.email || undefined,
    url: b.website || undefined,
    address: b.address ? { "@type": "PostalAddress", streetAddress: b.address, addressLocality: "Sanluri", addressRegion: "SU", addressCountry: "IT" } : undefined,
    geo: (b.latitude && b.longitude) ? { "@type": "GeoCoordinates", latitude: b.latitude, longitude: b.longitude } : undefined,
  };

  // Gallery layout: up to 5 tiles visible on desktop.
  // 1 large cover (col-span-2 row-span-2) + 4 smaller tiles (2×2 grid on the right).
  // Overflow indicator on last visible tile when more items exist.
  const gridMax = 5;
  const visible = mediaItems.slice(0, gridMax);
  const hiddenCount = Math.max(0, mediaItems.length - gridMax);

  return (
    <div className="pt-32 pb-8" data-testid="listing-detail">
      <PageSEO
        seo={{
          title: b.seo_title,
          description: b.seo_description,
          og_image: b.seo_og_image || cover,
          canonical_url: b.canonical_url,
          noindex: b.status !== "published",
        }}
        defaults={{
          title: `${b.name} · CCN Sanluri`,
          description: b.short_description || `Scopri ${b.name} sul portale del Centro Commerciale Naturale di Sanluri.`,
          ogImage: cover,
        }}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(jsonLd) }} />

      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Breadcrumb items={[
          { label: "Home", to: "/" },
          { label: "Attività", to: "/attivita" },
          ...(b.category ? [{ label: b.category.name, to: `/categoria/${b.category.slug}` }] : []),
          { label: b.name },
        ]} />

        <div className="mt-6 relative rounded-[32px] overflow-hidden bg-slate-100">
          <div className="aspect-[21/8] md:aspect-[21/7]"><img src={cover} alt={b.name} loading="eager" fetchPriority="high" className="w-full h-full object-cover" /></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 text-white">
            <div className="flex items-end gap-5">
              {b.logo_url && (
                <div className="w-20 h-20 md:w-28 md:h-28 rounded-2xl bg-white p-2 shadow-xl flex-shrink-0"><img src={b.logo_url} alt="" loading="lazy" className="w-full h-full object-cover rounded-xl" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {b.category?.name && <Link to={`/categoria/${b.category.slug}`} className="text-[11px] uppercase tracking-[0.18em] bg-white/15 backdrop-blur border border-white/20 px-2.5 py-1 rounded-full">{b.category.name}</Link>}
                  {(b.business_subcategories || []).slice(0, 3).map((bs) => bs.subcategory && (
                    <Link key={bs.subcategory.slug} to={`/categoria/${b.category?.slug}/${bs.subcategory.slug}`} className="text-[11px] uppercase tracking-[0.18em] bg-white/15 backdrop-blur border border-white/20 px-2.5 py-1 rounded-full">{bs.subcategory.name}</Link>
                  ))}
                </div>
                <h1 className="font-display font-bold text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">{b.name}</h1>
                {b.slogan && <p className="text-lg md:text-xl mt-2 text-white/90 italic">“{b.slogan}”</p>}
                {status && (
                  <div className="mt-4 inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/20 rounded-full px-3 py-1.5 text-sm">
                    <span className={`w-2 h-2 rounded-full ${status.open ? "bg-[#6FC247]" : "bg-red-400"}`} />
                    {status.label}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* CTA rapidi */}
        <div className="mt-6 flex flex-wrap gap-2">
          {show("phone") && <ActionBtn href={`tel:${b.phone}`} icon={Phone} label="Chiama" primary />}
          {vis.whatsapp !== false && wa && <ActionBtn href={`https://wa.me/${wa}`} icon={MessageCircle} label="WhatsApp" />}
          {mapsUrl && <ActionBtn href={mapsUrl} icon={MapPin} label="Indicazioni" />}
          {show("website") && <ActionBtn href={b.website} icon={Globe} label="Sito" />}
          {show("booking_url") && <ActionBtn href={b.booking_url} icon={Ticket} label="Prenota" />}
          {show("ecommerce_url") && <ActionBtn href={b.ecommerce_url} icon={ShoppingBag} label="Shop" />}
          {show("menu_url") && <ActionBtn href={b.menu_url} icon={BookOpen} label="Menu" />}
          <button onClick={copyLink} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm bg-white text-[#1F2937] border border-[#E5E7EB] hover:border-[#12964A]" data-testid="listing-share">
            {copied ? <><Check className="w-4 h-4" /> Copiato</> : <><Share2 className="w-4 h-4" /> Condividi</>}
          </button>
        </div>
      </section>

      {/* GALLERY (integra Video come ultimo elemento) */}
      {mediaItems.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-16" data-testid="listing-gallery">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Galleria</div>
              <h2 className="font-display font-bold text-3xl md:text-4xl text-[#1F2937]">Uno sguardo dentro</h2>
            </div>
            {mediaItems.length > 1 && (
              <button onClick={() => setLightbox(0)} className="hidden md:inline-flex items-center gap-1.5 text-sm text-[#12964A] hover:underline">
                <Camera className="w-4 h-4" /> Sfoglia tutte ({mediaItems.length})
              </button>
            )}
          </div>

          {/* Desktop mosaic */}
          <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-3 h-[520px]">
            {visible.map((it, i) => {
              const isCover = i === 0;
              const isLast = i === visible.length - 1;
              return (
                <GalleryTile
                  key={i}
                  item={it}
                  onClick={() => setLightbox(i)}
                  className={`rounded-2xl ${isCover ? "col-span-2 row-span-2" : ""}`}
                  moreCount={isLast && hiddenCount > 0 ? hiddenCount : 0}
                />
              );
            })}
          </div>

          {/* Mobile: horizontal snap carousel */}
          <div className="md:hidden -mx-6 px-6 flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2" style={{ scrollbarWidth: "none" }}>
            {mediaItems.map((it, i) => (
              <div key={i} className="snap-start flex-shrink-0 w-[82%] first:pl-0">
                <GalleryTile item={it} onClick={() => setLightbox(i)} className="rounded-2xl h-[300px] w-full block" />
              </div>
            ))}
          </div>
        </section>
      )}

      <Lightbox
        items={mediaItems}
        index={lightbox}
        onClose={() => setLightbox(-1)}
        onPrev={() => setLightbox((i) => (i - 1 + mediaItems.length) % mediaItems.length)}
        onNext={() => setLightbox((i) => (i + 1) % mediaItems.length)}
        onSelect={(i) => setLightbox(i)}
      />

      {/* CONTENT + SIDEBAR */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-16">
          {/* Chi siamo */}
          {(b.full_description || b.short_description) && (
            <div data-testid="section-about">
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Chi siamo</div>
              <h2 className="font-display font-bold text-3xl md:text-4xl text-[#1F2937] mb-6">La nostra storia</h2>
              <div className="prose prose-lg max-w-none text-lg text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderMarkdown(b.full_description || b.short_description) }} />
            </div>
          )}

          {/* Servizi e caratteristiche */}
          {services.length > 0 && (
            <div data-testid="section-services">
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Servizi</div>
              <h2 className="font-display font-bold text-3xl md:text-4xl text-[#1F2937] mb-6">Cosa offriamo</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                {services.map((s) => (
                  <div key={s} className="flex items-center gap-2 bg-[#F0FAF3] text-[#0E7C3D] rounded-xl px-4 py-3 text-sm font-medium">
                    <Check className="w-4 h-4 flex-shrink-0" /> {s}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mappa */}
          {osmSrc && (
            <div data-testid="section-map">
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Dove siamo</div>
              <h2 className="font-display font-bold text-3xl md:text-4xl text-[#1F2937] mb-6">In centro a Sanluri</h2>
              <div className="aspect-[21/9] rounded-3xl overflow-hidden border border-[#E5E7EB] shadow-sm">
                <iframe title="Mappa" src={osmSrc} className="w-full h-full" loading="lazy" />
              </div>
              {b.address && <p className="mt-4 text-slate-600">{b.address}</p>}
              {mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-sm text-[#12964A] hover:underline">Ottieni indicazioni →</a>}
            </div>
          )}
        </div>

        {/* SIDEBAR */}
        <aside className="lg:col-span-4 space-y-4">
          <div className="bg-white rounded-3xl border border-[#E5E7EB] p-6 lg:sticky lg:top-28" data-testid="section-contacts">
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-4">Contatti</div>
            <div className="space-y-3.5 text-sm">
              {show("address") !== false && b.address && <ContactRow icon={MapPin} label="Indirizzo" value={b.address} href={mapsUrl} />}
              {show("phone") && <ContactRow icon={Phone} label="Telefono" value={b.phone} href={`tel:${b.phone}`} />}
              {vis.whatsapp !== false && wa && <ContactRow icon={MessageCircle} label="WhatsApp" value={b.whatsapp} href={`https://wa.me/${wa}`} />}
              {show("email") && <ContactRow icon={Mail} label="Email" value={b.email} href={`mailto:${b.email}`} />}
              {show("website") && <ContactRow icon={Globe} label="Sito web" value={b.website?.replace(/https?:\/\/(www\.)?/, "")} href={b.website} />}
            </div>

            {/* Social */}
            {(show("instagram_url") || show("facebook_url") || show("tiktok_url") || show("youtube_url") || show("linkedin_url")) && (
              <div className="mt-6 pt-6 border-t border-[#F1F5F9]">
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-4">Social</div>
                <div className="flex flex-wrap gap-2">
                  {show("instagram_url") && <SocialBtn icon={Instagram} href={b.instagram_url} label="Instagram" />}
                  {show("facebook_url") && <SocialBtn icon={Facebook} href={b.facebook_url} label="Facebook" />}
                  {show("tiktok_url") && <SocialBtn icon={Music} href={b.tiktok_url} label="TikTok" />}
                  {show("youtube_url") && <SocialBtn icon={Youtube} href={b.youtube_url} label="YouTube" />}
                  {show("linkedin_url") && <SocialBtn icon={Linkedin} href={b.linkedin_url} label="LinkedIn" />}
                </div>
              </div>
            )}
          </div>

          {b.opening_hours && Object.values(b.opening_hours).some((d) => d?.mode && d.mode !== "closed") && (
            <div className="bg-white rounded-3xl border border-[#E5E7EB] p-6" data-testid="section-hours">
              <button onClick={() => setHoursOpen((v) => !v)} className="w-full flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Orari</div>
                <span className="inline-flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-[#12964A]" />
                  {status.open ? <span className="text-[#0E7C3D] font-medium">Aperto</span> : <span className="text-slate-500">Chiuso</span>}
                </span>
              </button>
              <div className="mt-3 text-sm text-slate-700">{status.label}</div>
              {hoursOpen && (
                <div className="mt-4 pt-4 border-t border-[#F1F5F9] space-y-1.5">
                  {DAYS.map((d) => <div key={d.key} className="flex justify-between text-sm"><span className="text-slate-600">{d.label}</span><span className="text-[#1F2937]">{formatDay(b.opening_hours[d.key])}</span></div>)}
                  {b.opening_hours_notes && <p className="text-xs text-slate-500 mt-3 italic">{b.opening_hours_notes}</p>}
                </div>
              )}
              <button onClick={() => setHoursOpen((v) => !v)} className="mt-3 text-xs text-[#12964A] hover:underline">{hoursOpen ? "Nascondi" : "Vedi orari settimanali"}</button>
            </div>
          )}
        </aside>
      </section>

      {related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24" data-testid="section-related">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Continua a scoprire</div>
          <h2 className="font-display font-bold text-3xl md:text-4xl text-[#1F2937] mb-8">Altre attività in {b.category?.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{related.map((r) => <ListingCard key={r.id} listing={r} />)}</div>
        </section>
      )}
    </div>
  );
};

const ContactRow = ({ icon: Icon, label, value, href }) => href ? (
  <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="flex items-start gap-3 hover:text-[#12964A] transition-colors group">
    <Icon className="w-4 h-4 mt-0.5 text-[#12964A] flex-shrink-0" />
    <div className="min-w-0"><div className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{label}</div><div className="text-[#1F2937] group-hover:text-[#12964A] truncate">{value}</div></div>
  </a>
) : null;

const SocialBtn = ({ icon: Icon, href, label }) => (
  <a href={href} target="_blank" rel="noreferrer" aria-label={label} title={label}
    className="w-10 h-10 rounded-full border border-[#E5E7EB] hover:border-[#12964A] hover:bg-[#F0FAF3] text-slate-600 hover:text-[#12964A] flex items-center justify-center transition-colors">
    <Icon className="w-4 h-4" />
  </a>
);

export default ListingDetail;
